import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import useAuthRoles from "../../../../common/hooks/useAuthRoles/useAuthRoles";
import {
  ActionIcon,
  Button,
  Flex,
  Group,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Tabs,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { fetcher } from "../../../../services/fetcher";
import DataTable from "../../../../common/components/DataTable/DataTable";
import LeadDrawer from "../../container/LeadDrawer";
import LeadFormModal from "../../container/LeadFormModal";
import NewQuotationModal from "../../container/NewQuotationModal";
import QuotationDrawer from "../../container/QuotationDrawer";
import LeadsOverview from "../../container/LeadsOverview";
import Bookings from "../Bookings/Bookings";
import Queries from "../Queries/Queries";
import FollowupDoneButton from "../../components/FollowupDoneButton/FollowupDoneButton";
import { FormatDate } from "../../../../helper/DateFormatting";
import {
  FOLLOWUP_PRIORITY_OPTIONS,
  FOLLOWUP_SOURCE_OPTIONS,
  FOLLOWUP_STATUS_OPTIONS,
  FOLLOWUP_TYPE_OPTIONS,
  QUOTATION_STATUS_OPTIONS,
  QUOTATION_TYPE_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
} from "../../utils/leadsOptions";

const PAGE_SIZE = 25;
const FOLLOWUP_PAGE_SIZE = PAGE_SIZE;
const CRM_SCOPE_OPTIONS = [
  { label: "Owner", value: "owner" },
  { label: "All", value: "all" },
];

const crmListUrl = (resource, scope, isAdmin, params) => {
  const base = isAdmin && scope === "all" ? `crm/${resource}/global/` : `crm/${resource}/`;
  return `${base}?${params.toString()}`;
};
const FOLLOWUP_MARK_COMPLETE_TOOLTIP =
  "Marks this follow-up as complete (not the lead).";

const displayCell = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    const nested = value.due_date ?? value.date ?? value.name ?? value.label;
    if (nested == null || nested === "" || typeof nested === "object") return null;
    return nested;
  }
  return value;
};

const followupDateLabel = (value) => {
  const raw = displayCell(value);
  if (raw == null) return null;
  const formatted = FormatDate(raw, "date");
  return formatted === "Invalid Date" ? String(raw) : formatted;
};

const crmToolbarFieldBorder = {
  border: "1px solid rgba(0, 83, 168, 0.35)",
  borderRadius: 8,
  backgroundColor: "#fff",
};

const followupFilterSelectProps = {
  allowDeselect: false,
  styles: {
    input: {
      ...crmToolbarFieldBorder,
      fontWeight: 500,
      color: "#033f7c",
    },
  },
};

const crmScopeSegmentProps = {
  size: "sm",
  radius: "md",
  w: 170,
  styles: {
    root: {
      ...crmToolbarFieldBorder,
      padding: 4,
      height: 42,
    },
    control: {
      height: 34,
      minHeight: 34,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    label: {
      fontWeight: 600,
      fontSize: 14,
      color: "#033f7c",
      paddingInline: 12,
      paddingBlock: 0,
      lineHeight: 1,
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    indicator: {
      backgroundColor: "#E7F0FF",
      boxShadow: "none",
    },
  },
};

const Leads = ({ initialTab = "leads" }) => {
  const { canViewSalesOverview: showOverviewTab, isAdminUser } = useAuthRoles();
  const [crmListScope, setCrmListScope] = useState("owner");
  const [opened, setOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [followupSearchQuery, setFollowupSearchQuery] = useState("");
  const [leadPage, setLeadPage] = useState(1);
  const [quotationPage, setQuotationPage] = useState(1);
  const [followupPage, setFollowupPage] = useState(1);
  const [followupOrdering, setFollowupOrdering] = useState("-due_date");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [followupsFetchEnabled, setFollowupsFetchEnabled] = useState(false);
  const [quotationsFetchEnabled, setQuotationsFetchEnabled] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [activeLead, setActiveLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [followupStatusFilter, setFollowupStatusFilter] = useState("");
  const [followupPriorityFilter, setFollowupPriorityFilter] = useState("");
  const [followupTypeFilter, setFollowupTypeFilter] = useState("");
  const [followupSourceFilter, setFollowupSourceFilter] = useState("");
  const [quotationTypeFilter, setQuotationTypeFilter] = useState("");
  const [quotationStatusFilter, setQuotationStatusFilter] = useState("");
  const [drawerInitialTab, setDrawerInitialTab] = useState("activities");
  const [newQuotationOpened, setNewQuotationOpened] = useState(false);
  const [quotationDrawerOpened, setQuotationDrawerOpened] = useState(false);
  const [activeQuotationId, setActiveQuotationId] = useState(null);
  const [quotationDrawerTab, setQuotationDrawerTab] = useState("overview");

  const leadsUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("ordering", "-created_on");
    params.set("page", String(leadPage));
    params.set("page_size", String(PAGE_SIZE));
    if (statusFilter) params.set("status", statusFilter);
    if (sourceFilter) params.set("source", sourceFilter);
    return crmListUrl("leads", crmListScope, isAdminUser, params);
  }, [crmListScope, isAdminUser, leadPage, statusFilter, sourceFilter]);

  const { data: leads, isLoading, mutate } = useSWR(leadsUrl, fetcher, {
    revalidateOnFocus: false,
  });

  useEffect(() => {
    if (activeTab === "followups") setFollowupsFetchEnabled(true);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "quotations") setQuotationsFetchEnabled(true);
  }, [activeTab]);

  useEffect(() => {
    const nextTab = initialTab || "leads";
    if (nextTab === "overview" && !showOverviewTab) {
      setActiveTab("leads");
      return;
    }
    setActiveTab(nextTab);
  }, [initialTab, showOverviewTab]);

  useEffect(() => {
    if (!showOverviewTab && activeTab === "overview") setActiveTab("leads");
  }, [activeTab, showOverviewTab]);

  const followupsUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("ordering", followupOrdering);
    params.set("page", String(followupPage));
    params.set("page_size", String(FOLLOWUP_PAGE_SIZE));
    if (followupStatusFilter) params.set("status", followupStatusFilter);
    if (followupPriorityFilter) params.set("priority", followupPriorityFilter);
    if (followupTypeFilter) params.set("followup_type", followupTypeFilter);
    if (followupSourceFilter) params.set("source", followupSourceFilter);
    const followupSearch = followupSearchQuery.trim();
    if (followupSearch) params.set("search", followupSearch);
    return crmListUrl("followups", crmListScope, isAdminUser, params);
  }, [
    crmListScope,
    followupOrdering,
    followupPage,
    followupPriorityFilter,
    followupSearchQuery,
    followupSourceFilter,
    followupStatusFilter,
    followupTypeFilter,
    isAdminUser,
  ]);

  useEffect(() => {
    setLeadPage(1);
    setFollowupPage(1);
    setQuotationPage(1);
  }, [crmListScope]);

  useEffect(() => {
    setLeadPage(1);
  }, [statusFilter, sourceFilter]);

  useEffect(() => {
    setQuotationPage(1);
  }, [quotationTypeFilter, quotationStatusFilter]);

  useEffect(() => {
    setFollowupPage(1);
  }, [
    followupOrdering,
    followupPriorityFilter,
    followupSearchQuery,
    followupSourceFilter,
    followupStatusFilter,
    followupTypeFilter,
  ]);

  const {
    data: followupsData,
    isLoading: isFollowupsLoading,
    mutate: mutateFollowups,
  } = useSWR(followupsFetchEnabled ? followupsUrl : null, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const quotationsUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("ordering", "-created_on");
    params.set("page", String(quotationPage));
    params.set("page_size", String(PAGE_SIZE));
    if (quotationTypeFilter) params.set("quotation_type", quotationTypeFilter);
    if (quotationStatusFilter) params.set("status", quotationStatusFilter);
    return crmListUrl("quotations", crmListScope, isAdminUser, params);
  }, [crmListScope, isAdminUser, quotationPage, quotationStatusFilter, quotationTypeFilter]);

  const {
    data: quotationsData,
    isLoading: isQuotationsLoading,
    mutate: mutateQuotations,
  } = useSWR(quotationsFetchEnabled ? quotationsUrl : null, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const leadItems = useMemo(() => {
    if (Array.isArray(leads?.results)) return leads.results;
    if (Array.isArray(leads?.data?.results)) return leads.data.results;
    if (Array.isArray(leads?.data)) return leads.data;
    if (Array.isArray(leads?.items)) return leads.items;
    if (Array.isArray(leads?.leads)) return leads.leads;
    if (Array.isArray(leads)) return leads;
    return [];
  }, [leads]);

  const followupItems = useMemo(() => {
    const raw = Array.isArray(followupsData?.results)
      ? followupsData.results
      : Array.isArray(followupsData?.data)
      ? followupsData.data
      : Array.isArray(followupsData)
      ? followupsData
      : [];

    return raw.map((item, index) => {
      const leadNo =
        item?.lead_no ||
        (item?.lead ? `LID-${String(item.lead).padStart(5, "0")}` : null);
      const isQuotation = Boolean(item?.quotation);
      const sourceKind = isQuotation ? "QUOTATION" : "LEAD";
      const sourceRef = isQuotation
        ? item?.quotation_no || String(item.quotation)
        : leadNo || "-";

      return {
        ...item,
        id: item?.id ?? index + 1,
        person_name: item?.person_name || item?.lead_name || "-",
        source_kind: sourceKind,
        source_ref: sourceRef,
        activity: item?.trek || "-",
      };
    });
  }, [followupsData]);

  const followupTotalCount = Number(followupsData?.count ?? followupItems.length) || 0;
  const followupTotalPages = Math.max(1, Math.ceil(followupTotalCount / FOLLOWUP_PAGE_SIZE));
  const followupRangeFrom = followupTotalCount
    ? (followupPage - 1) * FOLLOWUP_PAGE_SIZE + 1
    : 0;
  const followupRangeTo = followupTotalCount
    ? Math.min(followupPage * FOLLOWUP_PAGE_SIZE, followupTotalCount)
    : 0;

  useEffect(() => {
    if (followupPage > followupTotalPages) {
      setFollowupPage(followupTotalPages);
    }
  }, [followupPage, followupTotalPages]);

  const followupActionHeader = useMemo(
    () => (
      <Group gap={4} wrap="nowrap" display="inline-flex" align="center" component="span">
        Action
        <Tooltip label={FOLLOWUP_MARK_COMPLETE_TOOLTIP} multiline w={220}>
          <ActionIcon
            variant="transparent"
            size="xs"
            color="#9BB3C7"
            aria-label="About mark complete"
          >
            <IconInfoCircle size={14} stroke={1.75} />
          </ActionIcon>
        </Tooltip>
      </Group>
    ),
    []
  );

  const followupConfig = useMemo(
    () => ({
      searchPlaceholder: "Search by name, phone, or lead ID...",
      searchInputWidth: 360,
      toolbarNoWrap: true,
      fields: [
        { key: "person_name", label: "Person", width: 140 },
        {
          key: "source_ref",
          label: "Source",
          width: 130,
          sortable: false,
          render: (row) => (
            <Stack gap={0} my="6px">
              <Text fz={10} fw={700} c="#9BB3C7" lh={1.2} style={{ letterSpacing: "0.02em" }}>
                {row?.source_kind}
              </Text>
              <Text fz={13} fw={500} c="#6C94B4" lh={1.3}>
                {row?.source_ref}
              </Text>
            </Stack>
          ),
        },
        { key: "phone", label: "Phone", width: 120 },
        {
          key: "activity",
          label: "Plan Name",
          width: 220,
          cellStyle: { whiteSpace: "normal" },
          render: (row) => (
            <Text size="sm" my="9px" c="#6C94B4" lh={1.35} style={{ whiteSpace: "normal" }}>
              {row?.activity || "-"}
            </Text>
          ),
        },
        { key: "followup_type", label: "Type", type: "status", width: 150 },
        { key: "priority", label: "Priority", type: "status", width: 110 },
        {
          key: "due_date",
          label: "Date",
          width: 110,
          render: (row) => (
            <Text
              size="sm"
              my="9px"
              fw={500}
              c={row?.status === "OVERDUE" ? "#EF4D57" : "#6C94B4"}
              style={{ whiteSpace: "nowrap" }}
            >
              {row?.due_date ? FormatDate(row.due_date, "date") : "-"}
            </Text>
          ),
        },
        { key: "status", label: "Status", type: "status", width: 120 },
        {
          key: "notes",
          label: "Notes",
          width: 200,
          sortable: false,
          cellStyle: { whiteSpace: "normal" },
          render: (row) =>
            row?.notes ? (
              <Text size="sm" my="9px" c="#6C94B4" lh={1.35} style={{ whiteSpace: "normal" }}>
                {row.notes}
              </Text>
            ) : (
              <Text size="sm" my="9px" c="#9BB3C7">—</Text>
            ),
        },
        {
          key: "action",
          label: followupActionHeader,
          sortable: false,
          width: 168,
          stopRowClick: true,
          cellStyle: { whiteSpace: "nowrap", verticalAlign: "middle" },
          render: (row) => (
            <Group gap={6} wrap="nowrap" my="4px" align="center" justify="flex-start">
              <FollowupDoneButton
                followup={row}
                followupId={row?.id}
                status={row?.status}
                onCompleted={mutateFollowups}
              />
              {row?.status !== "DONE" ? (
                <Tooltip label={FOLLOWUP_MARK_COMPLETE_TOOLTIP} multiline w={220}>
                  <ActionIcon
                    variant="transparent"
                    size="sm"
                    color="#9BB3C7"
                    aria-label="About mark complete"
                  >
                    <IconInfoCircle size={16} stroke={1.75} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
            </Group>
          ),
        },
      ],
    }),
    [followupActionHeader, mutateFollowups]
  );

  const quotationItems = useMemo(() => {
    if (Array.isArray(quotationsData?.results)) return quotationsData.results;
    if (Array.isArray(quotationsData?.data)) return quotationsData.data;
    if (Array.isArray(quotationsData)) return quotationsData;
    return [];
  }, [quotationsData]);

  const quotationsConfig = {
    fields: [
      { key: "quot_no", label: "Quotation No." },
      { key: "lead_display", label: "Lead" },
      { key: "customer_name", label: "Customer" },
      { key: "quotation_type", label: "Type" },
      { key: "travel_route", label: "Travel Route" },
      { key: "start_date", label: "Start Date", type: "date" },
      { key: "end_date", label: "End Date", type: "date" },
      { key: "persons", label: "Pax" },
      { key: "plan_name", label: "Plan Name" },
      { key: "final_premium", label: "Final Premium" },
      { key: "status", label: "Status", type: "status" },
      { key: "created_on", label: "Created", type: "date" },
      {
        key: "open_link",
        label: "Preview",
        stopRowClick: true,
        width: 120,
        render: (row) => {
          const link = row.unique_link || row.quote_link;
          if (link) {
            return (
              <Button
                size="xs"
                color="green"
                onClick={() => window.open(link, "_blank", "noopener,noreferrer")}
              >
                Open
              </Button>
            );
          }
          return (
            <Text
              size="sm"
              c="orange"
              fw={500}
              style={{ cursor: "pointer" }}
              onClick={() => {
                setActiveQuotationId(row.id);
                setQuotationDrawerTab("payment-link");
                setQuotationDrawerOpened(true);
              }}
            >
              Link
            </Text>
          );
        },
      },
    ],
  };

  const filteredLeads = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const normalizedRows = leadItems.map((lead, index) => ({
      ...lead,
      id: lead?.id ?? index + 1,
      lead_no: lead?.lead_no || (lead?.id ? `LID-${String(lead.id).padStart(4, "0")}` : "-"),
      name: lead?.name || lead?.lead_name || lead?.customer_name || "-",
      sales_person: lead?.assigned_to?.name || "-",
      trek: lead?.trek || lead?.plan_name || lead?.plan || "-",
      persons: lead?.persons ?? lead?.no_of_people ?? lead?.pax ?? "-",
      lead_type: lead?.lead_type || "-",
      source: lead?.source || lead?.lead_source || "-",
      created_on: lead?.created_on || lead?.created_at || "-",
      next_followup_date:
        followupDateLabel(lead?.next_followup_date) ||
        followupDateLabel(lead?.next_followup) ||
        followupDateLabel(lead?.followup_date) ||
        "-",
      last_contacted_date:
        displayCell(lead?.last_contacted_date) ||
        displayCell(lead?.last_contacted) ||
        displayCell(lead?.updated_at) ||
        "-",
    }));
    if (!query) return normalizedRows;
    return normalizedRows.filter((lead) => {
      const fields = [
        lead?.lead_no,
        lead?.name,
        lead?.sales_person,
        lead?.phone,
        lead?.email,
        lead?.trek,
        lead?.status,
        lead?.source,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      return fields.some((value) => value.includes(query));
    });
  }, [leadItems, searchQuery]);

  const quotationsRows = useMemo(
    () =>
      quotationItems.map((item, index) => ({
        ...item,
        id: item?.id ?? index + 1,
        lead_id: item?.lead_id ?? item?.lead ?? item?.lead_obj?.id,
        lead_no: item?.lead_no || "",
        lead_display: item?.lead_no || item?.lead_name || "-",
        travel_route:
          [item?.travelling_from, item?.travelling_to]
            .filter(Boolean)
            .join(" - ") || "-",
        final_premium: item?.final_premium ? `₹ ${item.final_premium}` : "-",
        unique_link: item?.unique_link || item?.quote_link || item?.quote || null,
      })),
    [quotationItems]
  );

  const filteredQuotations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return quotationsRows;
    return quotationsRows.filter((row) => {
      const fields = [
        row?.quot_no,
        row?.lead_no,
        row?.lead_display,
        row?.customer_name,
        row?.plan_name,
        row?.travel_route,
        row?.status,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      return fields.some((value) => value.includes(query));
    });
  }, [quotationsRows, searchQuery]);

  const leadTotalCount = Number(leads?.count) || 0;
  const leadTotalPages = Math.max(1, Math.ceil(leadTotalCount / PAGE_SIZE));
  const leadRangeFrom = leadTotalCount ? (leadPage - 1) * PAGE_SIZE + 1 : 0;
  const leadRangeTo = leadTotalCount ? Math.min(leadPage * PAGE_SIZE, leadTotalCount) : 0;
  const quotationTotalCount = Number(quotationsData?.count) || 0;
  const quotationTotalPages = Math.max(1, Math.ceil(quotationTotalCount / PAGE_SIZE));
  const quotationRangeFrom = quotationTotalCount ? (quotationPage - 1) * PAGE_SIZE + 1 : 0;
  const quotationRangeTo = quotationTotalCount
    ? Math.min(quotationPage * PAGE_SIZE, quotationTotalCount)
    : 0;

  useEffect(() => {
    if (leads && leadPage > leadTotalPages) setLeadPage(leadTotalPages);
  }, [leads, leadPage, leadTotalPages]);

  useEffect(() => {
    if (quotationsData && quotationPage > quotationTotalPages) setQuotationPage(quotationTotalPages);
  }, [quotationsData, quotationPage, quotationTotalPages]);

  const handleRowClick = (row) => {
    setDrawerInitialTab("activities");
    setActiveLead(row);
    setDrawerOpened(true);
  };

  const handleQuotationRowClick = (row) => {
    if (!row?.id) return;
    setActiveQuotationId(row.id);
    setQuotationDrawerTab("overview");
    setQuotationDrawerOpened(true);
  };

  const config = {
    fields: [
      { key: "lead_no", label: "LEAD NO" },
      { key: "name", label: "CUSTOMER" },
      { key: "phone", label: "PHONE" },
      { key: "trek", label: "Plan Name" },
      { key: "persons", label: "PAX" },
      { key: "lead_type", label: "TYPE" },
      { key: "status", label: "STATUS", type: "status" },
      { key: "source", label: "SOURCE" },
      { key: "next_followup_date", label: "NEXT FOLLOW-UP" },
      { key: "last_contacted_date", label: "LAST CONTACT" },
      { key: "sales_person", label: "SALES PERSON" },
      { key: "created_on", label: "CREATED", type: "date" },
    ],
    selectionActions: [
      {
        label: "+ New Lead",
        handler: () => {
          setOpened(true);
        },
      },
    ],
    onRowClick: handleRowClick,
    doubleClickRow: true,
  };

  const adminScopeTabs = isAdminUser ? (
    <SegmentedControl
      {...crmScopeSegmentProps}
      value={crmListScope}
      onChange={setCrmListScope}
      data={CRM_SCOPE_OPTIONS}
    />
  ) : null;

  const leadsToolbarFilters = (
    <Group gap="sm" wrap="nowrap">
      {adminScopeTabs}
      <Select
        placeholder="All Statuses"
        value={statusFilter || null}
        onChange={(value) => setStatusFilter(value || "")}
        data={[
          { value: "", label: "All Statuses" },
          ...STATUS_OPTIONS.map((item) => ({ value: item.value, label: item.label })),
        ]}
        w={170}
        allowDeselect={false}
      />
      <Select
        placeholder="All Sources"
        value={sourceFilter || null}
        onChange={(value) => setSourceFilter(value || "")}
        data={[
          { value: "", label: "All Sources" },
          ...SOURCE_OPTIONS.map((item) => ({ value: item.value, label: item.label })),
        ]}
        w={150}
        allowDeselect={false}
      />
    </Group>
  );

  const followupsToolbarFilters = (
    <Group gap="sm" wrap="nowrap">
      {adminScopeTabs}
      <Select
        placeholder="All Statuses"
        value={followupStatusFilter || null}
        onChange={(value) => setFollowupStatusFilter(value || "")}
        data={[
          { value: "", label: "All Statuses" },
          ...FOLLOWUP_STATUS_OPTIONS.map((item) => ({
            value: item.value,
            label: item.label,
          })),
        ]}
        w={165}
        {...followupFilterSelectProps}
      />
      <Select
        placeholder="All Priorities"
        value={followupPriorityFilter || null}
        onChange={(value) => setFollowupPriorityFilter(value || "")}
        data={[
          { value: "", label: "All Priorities" },
          ...FOLLOWUP_PRIORITY_OPTIONS.map((item) => ({
            value: item.value,
            label: item.label,
          })),
        ]}
        w={165}
        {...followupFilterSelectProps}
      />
      <Select
        placeholder="All Types"
        value={followupTypeFilter || null}
        onChange={(value) => setFollowupTypeFilter(value || "")}
        data={[
          { value: "", label: "All Types" },
          ...FOLLOWUP_TYPE_OPTIONS.map((item) => ({
            value: item.value,
            label: item.label,
          })),
        ]}
        w={185}
        {...followupFilterSelectProps}
      />
      <Select
        placeholder="Lead & Quotation"
        value={followupSourceFilter || null}
        onChange={(value) => setFollowupSourceFilter(value || "")}
        data={[
          { value: "", label: "Lead & Quotation" },
          ...FOLLOWUP_SOURCE_OPTIONS.map((item) => ({
            value: item.value,
            label: item.label,
          })),
        ]}
        w={170}
        {...followupFilterSelectProps}
      />
    </Group>
  );

  const quotationsToolbarFilters = (
    <Group gap="sm" wrap="nowrap">
      {adminScopeTabs}
      <Select
        placeholder="All Types"
        value={quotationTypeFilter || null}
        onChange={(value) => setQuotationTypeFilter(value || "")}
        data={[
          { value: "", label: "All Types" },
          ...QUOTATION_TYPE_OPTIONS.map((item) => ({
            value: item.value,
            label: item.label,
          })),
        ]}
        w={165}
        allowDeselect={false}
      />
      <Select
        placeholder="All Statuses"
        value={quotationStatusFilter || null}
        onChange={(value) => setQuotationStatusFilter(value || "")}
        data={[
          { value: "", label: "All Statuses" },
          ...QUOTATION_STATUS_OPTIONS.map((item) => ({
            value: item.value,
            label: item.label,
          })),
        ]}
        w={165}
        allowDeselect={false}
      />
    </Group>
  );

  const quotationsTableConfig = {
    ...quotationsConfig,
    onRowClick: handleQuotationRowClick,
    doubleClickRow: true,
    selectionActions: [
      {
        label: "+ New Quotation",
        handler: () => setNewQuotationOpened(true),
      },
    ],
    selectionActionButtonProps: {
      variant: "filled",
      color: "orange",
    },
  };

  return (
    <Stack>
      <Tabs value={activeTab} onChange={setActiveTab} variant="pills" radius="xl">
        <Tabs.List>
          {showOverviewTab ? <Tabs.Tab value="overview">Overview</Tabs.Tab> : null}
          <Tabs.Tab value="leads">Leads</Tabs.Tab>
          <Tabs.Tab value="queries">Queries</Tabs.Tab>
          <Tabs.Tab value="followups">Follow-ups</Tabs.Tab>
          <Tabs.Tab value="quotations">Quotations</Tabs.Tab>
          <Tabs.Tab value="booking">Booking</Tabs.Tab>
        </Tabs.List>

        {showOverviewTab ? (
          <Tabs.Panel value="overview" pt="md">
            <LeadsOverview
              onViewAllFollowups={() => setActiveTab("followups")}
            />
          </Tabs.Panel>
        ) : null}

        <Tabs.Panel value="leads" pt="md">
          <DataTable
            data={filteredLeads}
            config={config}
            loading={isLoading}
            onSearch={(event) => setSearchQuery(event.target.value)}
            searchValue={searchQuery}
            toolbarLeftContent={leadsToolbarFilters}
          />
          <Flex justify="space-between" align="center" mt="md" px={4}>
            <Text size="sm" fw={600} c="#6C94B4">
              {leadTotalCount
                ? `Showing ${leadRangeFrom}–${leadRangeTo} of ${leadTotalCount} leads`
                : "Showing 0 leads"}
            </Text>
            <Pagination
              total={leadTotalPages}
              value={leadPage}
              onChange={setLeadPage}
              radius="sm"
              size="sm"
              siblings={1}
            />
          </Flex>
        </Tabs.Panel>

        <Tabs.Panel value="queries" pt="md" keepMounted={false}>
          <Queries
            listScope={crmListScope}
            isAdminUser={isAdminUser}
            scopeTabs={adminScopeTabs}
          />
        </Tabs.Panel>

        <Tabs.Panel value="followups" pt="md">
          <DataTable
            data={followupItems}
            config={followupConfig}
            loading={isFollowupsLoading}
            toolbarLeftContent={followupsToolbarFilters}
            searchPlaceholder={followupConfig.searchPlaceholder}
            searchValue={followupSearchQuery}
            onSearch={(event) => setFollowupSearchQuery(event.target.value)}
            onOrdering={setFollowupOrdering}
            orderingValue={followupOrdering}
          />
          <Flex justify="space-between" align="center" mt="md" px={4}>
            <Text size="sm" fw={600} c="#6C94B4">
              {followupTotalCount
                ? `Showing ${followupRangeFrom}–${followupRangeTo} of ${followupTotalCount} follow-ups`
                : "Showing 0 follow-ups"}
            </Text>
            <Pagination
              total={followupTotalPages}
              value={followupPage}
              onChange={setFollowupPage}
              radius="sm"
              size="sm"
              siblings={1}
            />
          </Flex>
        </Tabs.Panel>

        <Tabs.Panel value="quotations" pt="md">
          <DataTable
            data={filteredQuotations}
            config={quotationsTableConfig}
            loading={isQuotationsLoading}
            onSearch={(event) => setSearchQuery(event.target.value)}
            searchValue={searchQuery}
            toolbarLeftContent={quotationsToolbarFilters}
          />
          <Flex justify="space-between" align="center" mt="md" px={4}>
            <Text size="sm" fw={600} c="#6C94B4">
              {quotationTotalCount
                ? `Showing ${quotationRangeFrom}–${quotationRangeTo} of ${quotationTotalCount} quotations`
                : "Showing 0 quotations"}
            </Text>
            <Pagination
              total={quotationTotalPages}
              value={quotationPage}
              onChange={setQuotationPage}
              radius="sm"
              size="sm"
              siblings={1}
            />
          </Flex>
        </Tabs.Panel>

        <Tabs.Panel value="booking" pt="md">
          <Bookings hideRowSelection />
        </Tabs.Panel>
      </Tabs>

      <LeadDrawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        lead={activeLead}
        initialTab={drawerInitialTab}
        onLeadUpdated={mutate}
      />

      <LeadFormModal
        opened={opened}
        onClose={() => setOpened(false)}
        onCreated={mutate}
      />

      <NewQuotationModal
        opened={newQuotationOpened}
        onClose={() => setNewQuotationOpened(false)}
        leads={leadItems}
        onCreated={mutateQuotations}
      />

      <QuotationDrawer
        opened={quotationDrawerOpened}
        onClose={() => setQuotationDrawerOpened(false)}
        quotationId={activeQuotationId}
        initialTab={quotationDrawerTab}
        onUpdated={mutateQuotations}
      />
    </Stack>
  );
};

export default Leads;